import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import LanguageSwitcher from './LanguageSwitcher';
import {
  LayoutDashboard, CalendarDays, Users, Scissors, UserCog,
  BarChart3, Settings, LogOut, Menu, X, Building2, MapPin, Receipt, HeartPulse, Megaphone, Box,
  ChevronsLeft, ChevronsRight, Sparkles, HelpCircle
} from 'lucide-react';

import { useState, useEffect } from 'react';

const navConfig = [
  { path: '/dashboard', key: 'dashboard', icon: LayoutDashboard, roles: ['admin'], perm: null },
  { path: '/appointments', key: 'appointments', icon: CalendarDays, roles: ['admin', 'staff'], perm: ['calendar', 'enabled'] },
  { path: '/clients', key: 'clients', icon: Users, roles: ['admin', 'staff'], perm: ['clients', 'enabled'] },
  { path: '/services', key: 'services', icon: Scissors, roles: ['admin', 'staff'], perm: ['services', 'enabled'] },
  { path: '/staff', key: 'staff', icon: UserCog, roles: ['admin', 'staff'], perm: ['team', 'enabled'] },
  { path: '/sales', key: 'sales', icon: Receipt, roles: ['admin', 'staff'], perm: ['sales', 'enabled'] },
  { path: '/locations', key: 'locations', icon: MapPin, roles: ['admin'] },
  { path: '/resources', key: 'resources', icon: Box, roles: ['admin'] },
  { path: '/retention', key: 'retention', icon: HeartPulse, roles: ['admin'] },
  { path: '/marketing', key: 'marketing', icon: Megaphone, roles: ['admin', 'staff'], perm: ['marketing', 'enabled'] },
  { path: '/reports', key: 'reports', icon: BarChart3, roles: ['admin', 'staff'], perm: ['reports', 'enabled'] },
  { path: '/settings', key: 'settings', icon: Settings, roles: ['admin', 'staff'], perm: ['settings', 'enabled'] },
  { path: '/ai-assistant', key: 'aiAssistant', icon: Sparkles, roles: ['admin'] },
  { path: '/help', key: 'help', icon: HelpCircle, roles: ['admin', 'staff'] },
];

const superAdminConfig = [
  { path: '/admin/dashboard', key: 'superAdmin', icon: Building2, roles: ['superadmin'] },
];

export default function Sidebar() {
  const { user, logout, hasPermission } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window !== 'undefined') return localStorage.getItem('sidebar_collapsed') === 'true';
    return false;
  });
  const [hovered, setHovered] = useState(false);
  const role = user?.role || 'staff';

  useEffect(() => {
    localStorage.setItem('sidebar_collapsed', collapsed);
  }, [collapsed]);

  const isSuperAdmin = role === 'superadmin';
  const allNav = isSuperAdmin
    ? [...superAdminConfig, ...navConfig.filter(i => i.roles.includes('admin'))]
    : navConfig.filter(item => {
        if (!item.roles.includes(role)) return false;
        if (item.perm && role === 'staff') return hasPermission(item.perm[0], item.perm[1]);
        return true;
      });

  const isExpanded = !collapsed || hovered;
  const sidebarWidth = isExpanded ? 'w-56' : 'w-[60px]';

  const navContent = (
    <div className="flex flex-col h-full">
      <div className={`border-b border-[#E5E7E1] flex items-center ${isExpanded ? 'p-5 justify-between' : 'p-3 justify-center'}`}>
        {isExpanded ? (
          <>
            <div>
              <h1 className="text-xl font-bold text-[#556B2F] tracking-tight" style={{ fontFamily: 'Outfit' }}>Apptio</h1>
              <p className="text-[10px] text-[#5C6656] tracking-wide uppercase font-medium">
                {isSuperAdmin ? 'Platform Admin' : 'Booking System'}
              </p>
            </div>
            <button onClick={() => setCollapsed(true)} className="hidden lg:flex p-1 rounded hover:bg-[#E9EDDF] text-[#5C6656]" data-testid="collapse-sidebar-btn">
              <ChevronsLeft size={16} />
            </button>
          </>
        ) : (
          <button onClick={() => setCollapsed(false)} className="hidden lg:flex p-1 rounded hover:bg-[#E9EDDF] text-[#556B2F]" data-testid="expand-sidebar-btn">
            <ChevronsRight size={16} />
          </button>
        )}
      </div>

      <nav className="flex-1 p-2 space-y-0.5 overflow-y-auto">
        {allNav.map(item => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          return (
            <NavLink key={item.path} to={item.path} data-testid={`nav-${item.key}`}
              onClick={() => setMobileOpen(false)}
              title={!isExpanded ? t(`sidebar.${item.key}`) : undefined}
              className={`flex items-center gap-3 rounded-lg text-sm font-medium transition-all duration-150 ${
                isExpanded ? 'px-3 py-2.5' : 'px-0 py-2.5 justify-center'
              } ${
                isActive ? 'bg-[#556B2F] text-white' : 'text-[#5C6656] hover:bg-[#E9EDDF] hover:text-[#1A2421]'
              }`}>
              <Icon size={18} strokeWidth={1.5} className="flex-shrink-0" />
              {isExpanded && <span className="truncate">{t(`sidebar.${item.key}`)}</span>}
            </NavLink>
          );
        })}
      </nav>

      <div className={`border-t border-[#E5E7E1] ${isExpanded ? 'p-3' : 'p-2'}`}>
        {isExpanded && (
          <>
            <div className="px-3 mb-2"><LanguageSwitcher /></div>
            <div className="px-3 py-2 mb-1">
              <p className="text-sm font-medium text-[#1A2421] truncate">{user?.name}</p>
              <p className="text-xs text-[#5C6656] truncate">{user?.email}</p>
              <span className={`inline-block mt-1 px-2 py-0.5 text-xs rounded-full font-medium capitalize ${
                isSuperAdmin ? 'bg-[#556B2F] text-white' : 'bg-[#E9EDDF] text-[#556B2F]'
              }`}>{isSuperAdmin ? 'Super Admin' : role}</span>
            </div>
          </>
        )}
        <button data-testid="logout-btn" onClick={logout}
          className={`flex items-center gap-2 w-full text-sm text-[#E2725B] hover:bg-red-50 rounded-lg transition-colors ${
            isExpanded ? 'px-3 py-2' : 'justify-center py-2'
          }`}
          title={!isExpanded ? 'Logout' : undefined}>
          <LogOut size={16} />
          {isExpanded && <span>{t('sidebar.logout')}</span>}
        </button>
      </div>
    </div>
  );

  return (
    <>
      <aside
        onMouseEnter={() => { if (collapsed) setHovered(true); }}
        onMouseLeave={() => setHovered(false)}
        className={`hidden lg:block fixed lg:static inset-y-0 left-0 z-40 ${sidebarWidth} bg-white border-r border-[#E5E7E1] transition-all duration-200 ${collapsed && hovered ? 'shadow-xl' : ''}`}
        style={{ minWidth: collapsed && !hovered ? '60px' : undefined }}>
        {navContent}
      </aside>
    </>
  );
}
